/**
 * Относительное время для списков кабинета.
 *
 * Зачем модуль. До него каждый список печатал абсолютную дату («14 авг, 16:00»)
 * своим экземпляром `Intl.DateTimeFormat`, и срочность приходилось вычислять в
 * голове: чтобы понять, просрочена ли работа, человек сравнивал две даты, одну
 * из которых ему не показали. Списки читают глазами по диагонали, и там нужно
 * «просрочено 3 дня», а точная дата пусть висит подсказкой.
 *
 * Три правила, которым подчиняются все ярлыки:
 *
 * 1. **Считаем календарные дни, а не сутки.** «Завтра в 09:00» наступает через
 *    16 часов, а «сегодня в 23:00» через 14, и делить на 86400000 значит
 *    называть первое сегодняшним. Поэтому разница берётся между полуночами
 * 2. **Пустого ярлыка не бывает.** Нет даты, возвращается текст про её
 *    отсутствие («срока нет», «не запланирован»). Слот в строке списка обязан
 *    быть занят всегда, иначе колонки соседних строк разъезжаются, ради чего
 *    вся эта работа и затевалась
 * 3. **Три тона, не больше.** `danger` только у просроченного, `accent` только
 *    у сегодняшнего, остальное без цвета. Это то же правило, по которому в
 *    расписании покрашены два состояния из десяти (`STATE_TONE` в
 *    `features/schedule/common.ts`): цветное перестаёт выделяться ровно тогда,
 *    когда цветного становится много
 *
 * ⚠️ Часы берутся клиентские, и это допустимо ровно потому, что здесь только
 * подпись. Права по этим величинам не решаются нигде: просрочку для отбора
 * считает сервер (`?state=overdue`), а `homeworkOverdue` из `@edu/shared`
 * отвечает на тот же вопрос для подсветки. Расхождение в минуты безобидно.
 */

import { dayCountText } from "./plurals.ts";

/**
 * Тон ярлыка. Ровно три значения, и `warn` среди них нет намеренно: «срок
 * завтра» это не то, ради чего стоит тратить четвёртый цвет, а вот
 * четырёхцветный список перестаёт читаться сразу.
 */
export type WhenTone = "danger" | "accent" | "none";

export interface WhenLabel {
  text: string;
  tone: WhenTone;
  /** Полная дата для `title`: подсказка под курсором, а не текст в строке. */
  title: string;
}

const timeFmt = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" });
const dayYearFmt = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const weekdayFmt = new Intl.DateTimeFormat("ru-RU", { weekday: "short" });
const fullFmt = new Intl.DateTimeFormat("ru-RU", {
  weekday: "short",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Полная дата словами: то, что уезжает в `title` у относительного ярлыка. */
export function absText(ts: number): string {
  return fullFmt.format(new Date(ts));
}

/** Время суток: «16:00». */
export function timeText(ts: number): string {
  return timeFmt.format(new Date(ts));
}

/**
 * Дата без времени, с годом только когда он не текущий. Год в каждой строке
 * это шум на девять месяцев из двенадцати, а его отсутствие у прошлогоднего
 * занятия это уже враньё.
 */
export function dayText(ts: number, now: number): string {
  const d = new Date(ts);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return (sameYear ? dayFmt : dayYearFmt).format(d);
}

/** Разница в календарных днях: положительная в будущее, отрицательная в прошлое. */
export function dayDiff(ts: number, now: number): number {
  const a = new Date(ts);
  a.setHours(0, 0, 0, 0);
  const b = new Date(now);
  b.setHours(0, 0, 0, 0);
  // Округление обязательно: между полуночями бывает 23 или 25 часов, когда
  // между ними лежит перевод часов. Без него весенняя суббота даёт 0.958 дня
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

/**
 * Срок работы.
 *
 * `active === false` означает «работа уже сдана»: тогда прошедший срок это не
 * тревога, а факт биографии, и красным он не красится. Иначе список
 * выполненных работ светился бы красным весь.
 */
export function dueLabel(dueAt: number | null, now: number, active = true): WhenLabel {
  if (dueAt === null) return { text: "срока нет", tone: "none", title: "" };

  const title = absText(dueAt);
  const diff = dayDiff(dueAt, now);

  if (diff < 0) {
    const late = -diff;
    return active
      ? { text: `просрочено ${dayCountText(late)}`, tone: "danger", title }
      : { text: `срок был ${dayText(dueAt, now)}`, tone: "none", title };
  }
  if (diff === 0) {
    return { text: `сегодня ${timeText(dueAt)}`, tone: active ? "accent" : "none", title };
  }
  if (diff === 1) return { text: `завтра ${timeText(dueAt)}`, tone: "none", title };
  if (diff <= 6) return { text: `через ${dayCountText(diff)}`, tone: "none", title };
  return { text: dayText(dueAt, now), tone: "none", title };
}

/**
 * Прошедшее событие: последнее занятие, дата сдачи, время правки.
 *
 * ⚠️ Тон здесь только `accent` у сегодняшнего и никакого у остального.
 * Прошлое красным не бывает: тревожиться о нём поздно.
 */
export function agoLabel(ts: number | null, now: number, absent = "никогда"): WhenLabel {
  if (ts === null) return { text: absent, tone: "none", title: "" };

  const title = absText(ts);
  const diff = dayDiff(ts, now);

  if (diff >= 0) return { text: `сегодня ${timeText(ts)}`, tone: "accent", title };
  const ago = -diff;
  if (ago === 1) return { text: `вчера ${timeText(ts)}`, tone: "none", title };
  if (ago <= 6) return { text: `${dayCountText(ago)} назад`, tone: "none", title };
  return { text: dayText(ts, now), tone: "none", title };
}

/**
 * Ближайшее будущее событие: следующее занятие ученика.
 *
 * Внутри недели показываем день недели с временем («чт 18:30»), а не «через 3
 * дня»: с недельным расписанием человек живёт по названиям дней, и «вторник»
 * он сопоставляет со своим календарём мгновенно, а «через 4 дня» пересчитывает.
 */
export function nextLabel(ts: number | null, now: number, absent = "не запланирован"): WhenLabel {
  if (ts === null) return { text: absent, tone: "none", title: "" };

  const title = absText(ts);
  const diff = dayDiff(ts, now);

  // Прошлое здесь означает разъехавшиеся часы или занятие, начавшееся минуту
  // назад. Показываем как сегодняшнее, а не как «через минус один день»
  if (diff <= 0) return { text: `сегодня ${timeText(ts)}`, tone: "accent", title };
  if (diff === 1) return { text: `завтра ${timeText(ts)}`, tone: "none", title };
  if (diff <= 6) {
    return { text: `${weekdayFmt.format(new Date(ts))} ${timeText(ts)}`, tone: "none", title };
  }
  return { text: `${dayText(ts, now)} ${timeText(ts)}`, tone: "none", title };
}

/**
 * Сколько осталось до автоматической уборки: корзина домашки и заготовок.
 *
 * Считается от даты удаления плюс срок хранения, а не от «сейчас»: у корзины
 * дата удаления и есть то единственное, что о строке известно.
 *
 * ⚠️ Глагола в тексте нет («сегодня», «через 3 дня»), потому что его несёт
 * заголовок колонки («Удалится», «Уборка»). С глаголом на телефоне, где слот
 * подписывает себя сам, выходило «Удалится: удалится через 3 дня».
 */
export function leftLabel(deletedAt: number, keepDays: number, now: number): WhenLabel {
  const at = deletedAt + keepDays * 86_400_000;
  const diff = dayDiff(at, now);
  const title = absText(at);
  if (diff <= 0) return { text: "сегодня", tone: "danger", title };
  // Последние трое суток красным: дальше уже нечего восстанавливать
  if (diff <= 3) return { text: `через ${dayCountText(diff)}`, tone: "danger", title };
  return { text: `через ${dayCountText(diff)}`, tone: "none", title };
}
